import { Link, useLocation } from "@tanstack/react-router";
import { useState } from "react";
import type { IconType } from "react-icons";
import {
  FiArchive,
  FiBell,
  FiCheckSquare,
  FiCode,
  FiHome,
  FiMusic,
  FiSearch,
  FiShieldOff,
  FiSmartphone,
} from "react-icons/fi";
import { SiPixiv } from "react-icons/si";
import { resolveHeaderMascot } from "@/app/layouts/headerMascot";
import { isHistoryPath } from "@/app/layouts/headerSearchVisibility";
import { openJukeboxWindow } from "@/app/layouts/jukeboxWindow";
import { SkipToMainContentLink } from "@/app/layouts/layoutAccessibility";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { AnnouncementNavLabel } from "@/features/announce/components/AnnouncementNavLabel";
import { useAnnouncementStatus } from "@/features/announce/hooks/useAnnouncementStatus";
import { useDesktopAnnouncementModal } from "@/features/announce/hooks/useDesktopAnnouncementModal";
import { SearchBar } from "@/features/catalog/components";
import { useCurrentUrlSlug } from "@/shared/hooks/useCurrentUrlSlug";
import { useIsPcForced } from "@/shared/hooks/useMediaQuery";
import { buildMobileVersionUrl } from "@/shared/lib/forcePc";
import { BOARD_RULES_HREF } from "@/shared/lib/mixReturn";
import { resolveThreadBoardScope } from "@/shared/lib/threadBoardScope";
import {
  isThreadRoutePath,
  parseMixRouteThreadSlug,
} from "@/shared/lib/threadRoutePath";

type AppPath =
  | "/"
  | "/announcements"
  | "/api-docs"
  | "/archive"
  | "/board"
  | "/jukebox"
  | "/search";

interface AppHeaderLink {
  label: string;
  icon: IconType;
  to: AppPath;
  announcement?: boolean;
  href?: never;
  external?: never;
}

interface AnchorHeaderLink {
  label: string;
  icon: IconType;
  href: string;
  external?: boolean;
  announcement?: never;
  to?: never;
}

type HeaderLink = AppHeaderLink | AnchorHeaderLink;

const NAV_LINKS: HeaderLink[] = [
  {
    label: "FANBOX",
    icon: SiPixiv,
    href: "https://aimoge.fanbox.cc/",
    external: true,
  },
  {
    label: "投票所",
    icon: FiCheckSquare,
    href: "https://vote.nijiurachan.net/",
    external: true,
  },
  { label: "ジュークボックス", icon: FiMusic, to: "/jukebox" },
];

// 「掲示板」「🏛️過去ログ」は各ページ内リンク（旧AI_BBS準拠のリンク列）に一本化するため、
// ヘッダー側からは削除する（#53: カタログでのリンク重複解消）。
const UTILITY_LINKS: HeaderLink[] = [
  {
    label: "告知",
    icon: FiBell,
    to: "/announcements",
    announcement: true,
  },
  {
    label: "利用規約",
    icon: FiShieldOff,
    // 板規約は`/mix/board_rules.html`で単一実体を配信し、全mountから
    // このURLを直指しする(BOARD_RULES_HREF参照)。板mount配下のURLに
    // すると nginx の thread renderer regex に吸い込まれるため。
    href: BOARD_RULES_HREF,
  },
  { label: "投稿検索", icon: FiSearch, to: "/search" },
  {
    label: "Wiki",
    icon: FiArchive,
    href: "https://wiki.nijiurachan.net/",
    external: true,
  },
  { label: "API", icon: FiCode, to: "/api-docs" },
  {
    label: "ホーム",
    icon: FiHome,
    href: "/",
  },
];

export const DesktopHeader: React.FunctionComponent = () => {
  const location = useLocation();
  const usesPageToolbar =
    location.pathname === "/" ||
    location.pathname === "/board" ||
    location.pathname === "/archive" ||
    location.pathname === "/search" ||
    isHistoryPath(location.pathname);
  // mixの板slug付きスレ(/mix/{slug}/thread/{id})もスレページとして扱う
  const isThreadView = isThreadRoutePath(location.pathname);
  // カタログ/リスト等はマウント全体の面(mixなら総合名)、スレページは個別板の面。
  // 対象板はrouterのpathnameから解決する(window.locationはpushState遅延で
  // 遷移直後のrenderに追いつかない。詳細はparseMixRouteThreadSlug)
  const displayName = isThreadView
    ? resolveThreadBoardScope(parseMixRouteThreadSlug(location.pathname))
        .displayName
    : BOARD_DISPLAY_NAME;
  const mascot = resolveHeaderMascot(useCurrentUrlSlug());
  const isPcForced = useIsPcForced();
  const [isJukeboxOpen, setIsJukeboxOpen] = useState(false);
  const { openAnnouncementModal } = useDesktopAnnouncementModal();
  const { hasUnreadAnnouncements, isAnnouncementMetaPending } =
    useAnnouncementStatus();

  return (
    <header className="desktop-header">
      <SkipToMainContentLink className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground" />

      <nav
        className="desktop-header-utilities col-start-3 row-start-1"
        aria-label="サイトメニュー"
      >
        {UTILITY_LINKS.filter(
          (link) => !(isThreadView && link.to === "/search"),
        ).map((link) => (
          <HeaderLinkItem
            key={link.label}
            link={link}
            showNew={link.announcement && hasUnreadAnnouncements}
            reserveNewBadge={link.announcement && isAnnouncementMetaPending}
            onClick={
              link.announcement
                ? (event: React.MouseEvent<HTMLAnchorElement>): void => {
                    if (
                      event.button !== 0 ||
                      event.ctrlKey ||
                      event.metaKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    openAnnouncementModal();
                  }
                : undefined
            }
          />
        ))}
        {/* ページ内ツールバーを持つ画面とスレッドでは、共通ヘッダーの検索を表示しない。 */}
        {!usesPageToolbar && !isThreadView && <SearchBar />}
      </nav>

      <p className="desktop-header-title col-start-2 row-start-1">
        <img
          className="desktop-header-mascot"
          src={mascot}
          alt=""
          aria-hidden="true"
        />
        {displayName}
      </p>

      <nav
        className="desktop-header-links col-start-1 row-start-1"
        aria-label="外部サービス"
      >
        {isPcForced && (
          <a
            className="desktop-header-link"
            href={buildMobileVersionUrl(window.location)}
          >
            <FiSmartphone aria-hidden="true" />
            スマホ版に戻る
          </a>
        )}
        {NAV_LINKS.map((link) => (
          <HeaderLinkItem
            key={link.label}
            link={link}
            onClick={
              link.to === "/jukebox"
                ? (event: React.MouseEvent<HTMLAnchorElement>): void => {
                    event.preventDefault();
                    setIsJukeboxOpen(true);
                  }
                : undefined
            }
          />
        ))}
      </nav>

      {isJukeboxOpen && (
        <output className="desktop-jukebox-notice" aria-live="polite">
          ジュークボックスは別ウィンドウで開きます。
          <button
            type="button"
            aria-label="ジュークボックスを別ウィンドウで開く"
            onClick={(): void => {
              openJukeboxWindow(window.open.bind(window));
            }}
          >
            開く
          </button>
          <button type="button" onClick={(): void => setIsJukeboxOpen(false)}>
            閉じる
          </button>
        </output>
      )}
    </header>
  );
};

interface HeaderLinkItemProps {
  link: HeaderLink;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  showNew?: boolean;
  reserveNewBadge?: boolean;
}

const HeaderLinkItem: React.FunctionComponent<HeaderLinkItemProps> = ({
  link,
  onClick,
  showNew = false,
  reserveNewBadge = false,
}: HeaderLinkItemProps) => {
  const Icon = link.icon;

  if (link.to) {
    return (
      <Link
        to={link.to}
        onClick={onClick}
        className={
          link.announcement
            ? "desktop-header-link min-h-11"
            : "desktop-header-link"
        }
      >
        <Icon aria-hidden="true" />
        {link.announcement ? (
          <AnnouncementNavLabel
            isNew={showNew}
            reserveNewBadge={reserveNewBadge}
          />
        ) : (
          link.label
        )}
      </Link>
    );
  }

  return (
    <a
      href={link.href}
      target={link.external ? "_blank" : undefined}
      rel={link.external ? "noopener noreferrer" : undefined}
      onClick={onClick}
      className="desktop-header-link"
    >
      <Icon aria-hidden="true" />
      {link.label}
    </a>
  );
};
